/**
 * Locale resolution — pure functions, no state.
 *
 * Language and direction come ONLY from an explicit choice (charter,
 * 2026-10-10: Arabic is opt-in): the person's own saved locale (Profile,
 * TenantUser.locale), else the language picked on this device (the sign-in
 * switch), else English. The workspace's locale and the browser's never pick
 * the language; they only supply formatting conventions (region), re-expressed
 * in the interface's language — an English screen in an ar-EG workspace
 * formats as en-EG, never with Arabic month or currency names.
 *
 * Digits: Arabic locales format with Latin digits (123) unless the saved tag
 * asks otherwise (e.g. `ar-EG-u-nu-arab`). Egyptian business documents,
 * the Egyptian Tax Authority's e-invoices, and the codes and document numbers
 * on every screen (JE-2026-000003, account 6200) are written in Latin digits;
 * ICU's default for ar-EG is Arabic-Indic (١٢٣), which would mix two digit
 * systems on one line. Month names, separators and currency names stay
 * Arabic.
 *
 * Calendar: Gregorian unless the tag asks otherwise. Some ICU versions default
 * ar-SA to the Umm al-Qura calendar, and Atlas does not show Hijri dates yet
 * (docs/help/voice.md); Hijri display comes through libs/localization-core.
 */
import type { Language } from "./types";

export const DEFAULT_LOCALE = "en-US";

/** A canonical BCP 47 tag, or null for anything Intl cannot read. */
export function canonicalLocale(tag: unknown): string | null {
  if (typeof tag !== "string" || tag.trim() === "") return null;
  try {
    return Intl.getCanonicalLocales(tag.trim())[0] ?? null;
  } catch {
    return null;
  }
}

function parse(tag: string): Intl.Locale | null {
  try {
    return new Intl.Locale(tag);
  } catch {
    return null;
  }
}

/** The catalog a locale reads: Arabic for any `ar` tag, English otherwise. */
export function languageOf(locale: string): Language {
  return parse(locale)?.language === "ar" ? "ar" : "en";
}

export function directionOf(locale: string): "ltr" | "rtl" {
  return languageOf(locale) === "ar" ? "rtl" : "ltr";
}

/**
 * The value for <html lang>: the language the TEXT is in. A French locale
 * reads the English catalog, so its page is lang="en" while its numbers are
 * still formatted the French way.
 */
export function htmlLangOf(locale: string): string {
  const loc = parse(locale);
  if (!loc) return "en";
  if (loc.language === "ar" || loc.language === "en") return loc.baseName;
  return "en";
}

/** The tag every formatter uses: Gregorian, and Latin digits for Arabic. */
export function formatLocaleOf(locale: string): string {
  const loc = parse(locale);
  if (!loc) return DEFAULT_LOCALE;
  try {
    return new Intl.Locale(loc.baseName, {
      calendar: loc.calendar ?? "gregory",
      numberingSystem:
        loc.numberingSystem ?? (loc.language === "ar" ? "latn" : undefined),
      hourCycle: loc.hourCycle,
    }).toString();
  } catch {
    return loc.baseName;
  }
}

/** An IANA zone Intl accepts, or undefined (the browser's own zone). */
export function validTimeZone(zone: unknown): string | undefined {
  if (typeof zone !== "string" || zone === "") return undefined;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return zone;
  } catch {
    return undefined;
  }
}

export function browserLocale(): string | null {
  if (typeof navigator === "undefined") return null;
  return canonicalLocale(navigator.languages?.[0] ?? navigator.language);
}

export interface LocaleSources {
  /** The person's own saved locale (Profile). An explicit choice. */
  person?: string | null;
  /** The language picked on this device (sign-in switch). An explicit choice. */
  device?: string | null;
  /** The workspace's locale: formatting conventions only, never the language. */
  workspace?: string | null;
  /** The browser's locale: formatting conventions only, never the language. */
  browser?: string | null;
}

/**
 * `locale` in another language, keeping its region: ar-EG read in English is
 * en-EG, en-US read in Arabic is ar-US. Unchanged when the language matches.
 */
export function withLanguage(locale: string, language: Language): string {
  const loc = parse(locale);
  if (!loc) return language;
  if (loc.language === language) return locale;
  return loc.region ? `${language}-${loc.region}` : language;
}

/**
 * The locale the interface runs in. The person's saved locale is used as it
 * is (a French locale reads English and formats French). Without one, the
 * language is the device's choice or English, and the formatting conventions
 * come from the workspace, else the device, else the browser.
 */
export function resolveLocale(sources: LocaleSources): string {
  const person = canonicalLocale(sources.person);
  if (person) return person;
  const device = canonicalLocale(sources.device);
  const conventions =
    canonicalLocale(sources.workspace) ??
    device ??
    canonicalLocale(sources.browser);
  if (!conventions) return DEFAULT_LOCALE;
  return withLanguage(conventions, device ? languageOf(device) : "en");
}

/**
 * The locale to switch to when someone picks a language by name (the sign-in
 * page's switch): the browser's own tag for that language if it has one,
 * otherwise Egypt's (the first market) or US English.
 */
export function localeForLanguage(language: Language): string {
  const preferred =
    typeof navigator === "undefined"
      ? []
      : (navigator.languages ?? [navigator.language]);
  for (const tag of preferred) {
    const canonical = canonicalLocale(tag);
    if (
      canonical &&
      languageOf(canonical) === language &&
      canonical.includes("-")
    )
      return canonical;
  }
  return language === "ar" ? "ar-EG" : DEFAULT_LOCALE;
}
